export const PBTCaseStatus = {
  Pending: 'Pending',
  DataGathering: 'Data Gathering',
  AwaitingAttestation: 'Date & Attestation Pending',
  AwaitingHrcApproval: 'Awaiting HRC Approval',
  ProcessingTerminationPacket: 'Preparing Termination Packet',
  Complete: 'Complete',
  DeliveryFailed: 'Delivery Failed',
  Paused: 'Paused',
  Cancelled: 'Cancelled',
  Removed: 'Removed',
} as const;
export type PBTCaseStatus = typeof PBTCaseStatus[keyof typeof PBTCaseStatus];

/** Maps a raw API status enum string (e.g. "AwaitingAttestation") to the UI
 * display string (e.g. "Date & Attestation Pending"). Idempotent: already-mapped UI
 * values (and aliases) pass through unchanged. */
const apiStatusMap: Record<string, PBTCaseStatus> = {
  // Match the API's LpmCaseMapper aliases so legacy stored values and dashboard
  // aggregate predicates resolve to the same displayed status.
  Pending: PBTCaseStatus.DataGathering,
  DataGathering: PBTCaseStatus.DataGathering,
  AwaitingAttestation: PBTCaseStatus.AwaitingAttestation,
  'Awaiting Attestation': PBTCaseStatus.AwaitingAttestation,
  AwaitingHrcApproval: PBTCaseStatus.AwaitingHrcApproval,
  ProcessingTerminationPacket: PBTCaseStatus.ProcessingTerminationPacket,
  Complete: PBTCaseStatus.Complete,
  DeliveryFailed: PBTCaseStatus.DeliveryFailed,
  // Backwards-compatible aliases (older API values)
  Active: PBTCaseStatus.AwaitingAttestation,
  ComplexCase: PBTCaseStatus.AwaitingAttestation,
  AwaitingManagerEngagement: PBTCaseStatus.DataGathering,
  Completed: PBTCaseStatus.Complete,
  Paused: PBTCaseStatus.Paused,
  Cancelled: PBTCaseStatus.Cancelled,
  Removed: PBTCaseStatus.Removed,
  Resigned: PBTCaseStatus.Removed,
};

export function mapApiStatusToUiStatus(raw: string | null | undefined): PBTCaseStatus {
  if (!raw) return raw as PBTCaseStatus;
  return (apiStatusMap[raw] || raw) as PBTCaseStatus;
}

export const PBTEngagementModel = {
  ManagerLed: 'Manager-led',
  ManagerHRCLed: 'Manager + HRC-led',
  HRCLed: 'HRC-led (manager bypassed)',
} as const;
export type PBTEngagementModel = typeof PBTEngagementModel[keyof typeof PBTEngagementModel];

export const PBTFlag = {
  GERWIT: 'GER/WIT',
  LOA: 'LOA',
  Overdue: 'Overdue',
  CrmMismatch: 'CRM Mismatch',
  NonResponsive: 'Non-Responsive',
  EmailFailed: 'Email Failed',
  ActionNeeded: 'Action Needed',
} as const;
export type PBTFlag = typeof PBTFlag[keyof typeof PBTFlag];

const HRC_LOA_REVIEWED_BIT = 1 << 8;
const HRC_GER_WIT_REVIEWED_BIT = 1 << 9;

/** Returns whether an HRC has reviewed and resumed this compliance-flagged case. */
export function isHrcLoaReviewed(flags: unknown): boolean {
  if (typeof flags === 'number') {
    return (flags & HRC_LOA_REVIEWED_BIT) !== 0;
  }

  const tokens = Array.isArray(flags)
    ? flags.map((token) => String(token))
    : typeof flags === 'string'
    ? flags.split(',')
    : [];
  const canon = (token: string): string => token.replace(/[^a-z0-9]/gi, '').toLowerCase();
  return tokens.some((token) => canon(token) === 'hrcloareviewed');
}

/** Returns whether an HRC has reviewed and resumed the current GER/WIT event. */
export function isHrcGerWitReviewed(flags: unknown): boolean {
  if (typeof flags === 'number') {
    return (flags & HRC_GER_WIT_REVIEWED_BIT) !== 0;
  }

  const tokens = Array.isArray(flags)
    ? flags.map((token) => String(token))
    : typeof flags === 'string'
    ? flags.split(',')
    : [];
  const canon = (token: string): string => token.replace(/[^a-z0-9]/gi, '').toLowerCase();
  return tokens.some((token) => canon(token) === 'hrcgerwitreviewed');
}

/**
 * Normalizes a raw flags value into a PBTFlag[] regardless of the wire shape.
 * The API may send flags as an integer bit-field (0-15), a comma-separated enum
 * string ("GerWit", "GerWit, Loa"), or an already-mapped array. Matching is
 * case-insensitive and punctuation-insensitive so "Loa", "LOA" and "GER/WIT" all resolve.
 */
export function normalizePbtFlags(flags: unknown): PBTFlag[] {
  if (flags === null || flags === undefined) return [];
  const result: PBTFlag[] = [];
  const push = (f: PBTFlag): void => {
    if (!result.includes(f)) result.push(f);
  };
  if (typeof flags === 'number') {
    if (flags & 1) push(PBTFlag.GERWIT);
    if (flags & 2) push(PBTFlag.LOA);
    if (flags & 4) push(PBTFlag.Overdue);
    if (flags & 8) push(PBTFlag.CrmMismatch);
    return result;
  }
  const tokens = Array.isArray(flags)
    ? flags.map((t) => String(t))
    : typeof flags === 'string'
    ? flags.split(',')
    : [];
  const canon = (t: string): string => t.replace(/[^a-z0-9]/gi, '').toLowerCase();
  tokens.forEach((t) => {
    switch (canon(t)) {
      case 'ger':
      case 'gerwit':
        push(PBTFlag.GERWIT);
        break;
      case 'loa':
        push(PBTFlag.LOA);
        break;
      case 'overdue':
        push(PBTFlag.Overdue);
        break;
      case 'crmmismatch':
        push(PBTFlag.CrmMismatch);
        break;
      case 'nonresponsive':
        push(PBTFlag.NonResponsive);
        break;
      case 'emailfailed':
        push(PBTFlag.EmailFailed);
        break;
      case 'actionneeded':
        push(PBTFlag.ActionNeeded);
        break;
    }
  });
  return result;
}

export const PBTDataStatus = {
  Complete: 'Complete',
  Blocked: 'Blocked',
  ActionNeeded: 'Action Needed',
} as const;
export type PBTDataStatus = typeof PBTDataStatus[keyof typeof PBTDataStatus];

export interface IPBTCase {
  id: number;
  empName: string;
  empAlias: string;
  pern: string;
  crmCase: string;
  status: PBTCaseStatus;
  engModel: PBTEngagementModel | (string & {}) | '';
  manager: string;
  managerAlias?: string;
  hrc: string;
  hrcName?: string;
  backupHrc: string | null;
  termConvDate: string | null;
  effTermDate: string | null;
  termEmailSentDate: string | null;
  daysInStatus: number;
  flags: PBTFlag[];
  hrcLoaReviewed?: boolean;
  hrcGerWitReviewed?: boolean;
  gerWitCheckStatus?: string | null;
  outcome: string | null;
  rehire: string | null;
  currentStep: number;
  dataStatus?: 'Complete' | 'Action Needed' | 'Blocked';
  workState?: string;
  homeState?: string;
  personalEmail?: string;
  immigration?: string;
  immigrationStatus?: 'Yes' | 'No' | 'Unknown – Manual Check Required';
  visaType?: string;
  sevenDay?: boolean;
  workAddr?: string;
  homeAddr?: string;
  geoRestricted?: boolean;
  emailFailed?: boolean;
  emailFailedDetail?: string;
  dateRescheduled?: boolean;
  originalTermDate?: string;
  termEmailSent?: boolean;
  pauseReason?: string;
  pauseReasonCode?: string;
  pauseSourceStatus?: string;
  pauseSourceChangedAt?: string;
  cancelReason?: string;
  kickoffEmailSent?: boolean;
  kickoffEmailSentAt?: string | null;
  managerName?: string;
  reminderCount?: number;
  manualReminderCount?: number;
  lastReminderSentAt?: string | null;
  managerNonResponsive?: boolean;
  hrcValidationReleasedBy?: string | null;
  hrcValidationPacketPending?: boolean;
  hrcValidationReleaseInProgress?: boolean;
  isManagerChanged?: boolean;
  isManagerChangeReviewed?: boolean;
}

export interface IPBTCaseAudit {
  auditId: number;
  pbtCaseId: number;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: string;
}

export interface IPBTCasesResponse {
  cases: IPBTCase[];
  totalCount: number;
}

export interface IUpdatePbtCaseRequest {
  CrmOutcome?: string;
  RehireEligibility?: string;
  EngagementModel?: string;
  BackupHrcAlias?: string;
  HrcAlias?: string;
}
